import React from 'react';
import { withSnackbar } from 'notistack';
import { flowRight } from 'lodash/fp';

import PageTabs from 'ui/PageTabs';
import Access from 'ui/Access';
import { isPartnerPortalEligible } from 'utils/user';

import Users from './users/Users';
import Groups from './groups/Groups';
import Shares from './shares/Shares';
import Devices from './devices/Devices';
import Policies from './policies/Policies';
import Connectors from './connectors/Connectors';
import Backups from './backups/Backups';
import PartnerPortal from './partner_portal/PartnerPortal';
import CompanyEdit from '../company/company/CompanyEdit';

import * as api from '../../api';
import { withAppContext } from '../AppContext';
import { CompanyContext } from './CompanyContext';
import Vaults from './vaults/Vaults';

function editableByResellerMSP(user, organization) {
    const user_org = user.organizations[0];
    if (user_org && user_org.type === 'reseller_msp') {
        return organization.uuid === user_org.uuid;
    }
    return true;
}

function accessibleChildTab(org, currentOrg) {
    if (currentOrg && currentOrg.type === 'reseller') {
        return currentOrg.uuid === org.uuid;
    }

    return true;
}

export function partnerTabAccess(org, currentOrg) {
    if (accessibleChildTab(org, currentOrg) && isPartnerPortalEligible(org)) {
        return partnerPortalAccess;
    }
    return null;
}

const partnerPortalAccess = [
    Access.COMPANIES_PARTNER_USERS,
    Access.COMPANIES_PARTNER_ROLES,
    Access.COMPANIES_PARTNER_COMPANY_GROUPS,
];

const TAB_LIST = [
    {
        name: 'users',
        label: 'Users',
        access: (org, currentOrg) => {
            if (accessibleChildTab(org, currentOrg)) {
                return Access.COMPANIES_USERS;
            }
        },
        component: Users,
    },
    {
        name: 'groups',
        label: 'Groups',
        access: (org, currentOrg) => {
            if (accessibleChildTab(org, currentOrg)) {
                return Access.COMPANIES_GROUPS;
            }
        },
        component: Groups,
    },
    {
        name: 'devices',
        label: 'Devices',
        access: (org, currentOrg) => {
            if (accessibleChildTab(org, currentOrg)) {
                return Access.COMPANIES_DEVICES;
            }
        },
        component: Devices,
    },
    {
        name: 'shares',
        label: 'Shares',
        access: (org, currentOrg) => {
            if (accessibleChildTab(org, currentOrg)) {
                return Access.COMPANIES_SHARES;
            }
        },
        component: Shares,
    },
    {
        name: 'vaults',
        label: 'Vaults',
        access: (org, currentOrg, pb6_authentication_enabled) => {
            const isOrgPB6Enabled = org.company_is_v6_enabled;
            const isUserPB6Enabled = pb6_authentication_enabled;
            if (
                isOrgPB6Enabled &&
                isUserPB6Enabled &&
                accessibleChildTab(org, currentOrg)
            ) {
                return Access.COMPANIES_VAULTS;
            }
        },
        component: Vaults,
    },
    {
        name: 'policies',
        label: 'Policies',
        access: (org, currentOrg) => {
            if (accessibleChildTab(org, currentOrg)) {
                return Access.COMPANIES_POLICIES;
            }
        },
        editable: editableByResellerMSP,
        component: Policies,
    },
    {
        name: 'connectors',
        label: 'Connectors',
        access: (org, currentOrg) => {
            if (accessibleChildTab(org, currentOrg)) {
                return Access.COMPANIES_CONNECTORS;
            }
        },
        component: Connectors,
    },
    {
        name: 'backups',
        label: 'Backups',
        access: (org, currentOrg) => {
            if (accessibleChildTab(org, currentOrg)) {
                return Access.COMPANIES_BACKUPS;
            }
        },
        editable: editableByResellerMSP,
        component: Backups,
    },
    {
        name: 'settings',
        label: 'Settings',
        access: Access.COMPANIES,
        component: CompanyEdit,
    },
    {
        name: 'partner',
        label: 'Partner Portal',
        access: partnerTabAccess,
        component: PartnerPortal,
    },
];

class CompanyDetail extends React.Component {
    state = {
        organization: {},
        tabs: [],
        tab: -1,
    };

    componentDidUpdate(oldProps) {
        const tabName = this.props.tab;
        const tab = this.findTabNumber(tabName);
        if (oldProps.tab !== tabName || tab !== this.state.tab) {
            this.setState({ tab });
        }
    }

    updateTabs(saveInState) {
        const { organization } = this.state;
        const { location } = this.props;
        const org =
            organization && organization.uuid
                ? organization
                : location.state && location.state.organization;
        const accessFilter = tab => this.canViewTab(tab.access, org);
        const tabs = TAB_LIST.filter(accessFilter);
        if (saveInState) {
            this.setState({ tabs });
        }
        return tabs;
    }

    findTabNumber(tabName) {
        const { tabs } = this.state;
        for (let i = 0; i < tabs.length; i += 1) {
            if (tabs[i].name === tabName) {
                return i;
            }
        }
        return 0;
    }

    findTab(num) {
        const { tabs } = this.state;
        return tabs[num] || tabs[0];
    }

    componentDidMount() {
        const { location } = this.props;
        const id = this.getCompanyId();

        if (location.state && location.state.organization) {
            const tabs = this.updateTabs(false);
            return this.setState(
                {
                    organization: location.state.organization,
                    tabs,
                },
                () => {
                    this.props.updateBreadcrumbs([
                        { name: 'Companies', path: '/companies' },
                        this.state.organization.business_name,
                    ]);
                }
            );
        }

        api.get(`/api/organizations/${id}`).then(json => {
            const [organization] = json.data;
            this.setState({ organization }, () => {
                this.updateTabs(true);
                this.props.updateBreadcrumbs([
                    { name: 'Companies', path: '/companies' },
                    this.state.organization.business_name,
                ]);
            });
        });
    }

    hasTab(num) {
        const { tabs } = this.state;
        return num < tabs.length;
    }

    findTabUrl(num) {
        const { organization } = this.state;
        if (this.hasTab(num)) {
            return `/companies/${organization.uuid}/${this.findTab(num).name}`;
        }
        return '';
    }

    getCompanyId() {
        const { match } = this.props;
        const { id } = match.params;
        return id;
    }

    canChangeTab(tabAccess, organization) {
        const { app } = this.props;
        const { currentUser } = app;
        if (typeof tabAccess === 'function') {
            tabAccess = tabAccess(
                organization,
                currentUser.organizations[0],
                currentUser.pb6_authentication_enabled
            );
        }
        if (Array.isArray(tabAccess)) {
            return tabAccess.some(access => app.canChange(access));
        }
        return app.canChange(tabAccess);
    }

    canViewTab(tabAccess, organization) {
        const { app } = this.props;
        const { currentUser } = app;
        if (typeof tabAccess === 'function') {
            tabAccess = tabAccess(
                organization,
                currentUser.organizations[0],
                currentUser
            );
        }
        if (Array.isArray(tabAccess)) {
            return tabAccess.some(access => app.canView(access));
        }
        return app.canView(tabAccess);
    }

    onTabChange = tab => {
        const { organization } = this.state;
        if (tab !== this.state.tab) {
            this.setState({ tab });
            if (this.hasTab(tab)) {
                this.props.history.push(this.findTabUrl(tab), { organization });
            }
        }
    };

    render() {
        const { tabs, organization, tab } = this.state;
        const { app, history, updateBreadcrumbs } = this.props;
        if (tab === -1) {
            return null;
        }
        return (
            <PageTabs
                tabs={tabs.map(tab => tab.label)}
                current={tab}
                onChange={this.onTabChange}
                darkBackground={[3]}
            >
                {() => {
                    const currentTab = this.findTab(tab);
                    if (!currentTab) {
                        return null;
                    }
                    const editable =
                        currentTab && currentTab.editable
                            ? currentTab.editable(app.currentUser, organization)
                            : true;
                    const ComponentTab = currentTab.component;
                    return (
                        <CompanyContext.Provider
                            value={CompanyContext.create(
                                this.getCompanyId(),
                                this.state.organization
                            )}
                        >
                            <ComponentTab
                                id={this.getCompanyId()}
                                tab={this.props.sub_tab}
                                history={history}
                                organization={organization}
                                updateBreadcrumbs={updateBreadcrumbs}
                                editable={
                                    this.canChangeTab(
                                        currentTab.access,
                                        organization
                                    ) && editable
                                }
                                groupsAccess={app.canView(
                                    Access.COMPANIES_GROUPS
                                )}
                                usersAccess={app.canView(
                                    Access.COMPANIES_USERS
                                )}
                                onMessage={this.props.enqueueSnackbar}
                            />
                        </CompanyContext.Provider>
                    );
                }}
            </PageTabs>
        );
    }
}

export default flowRight(withSnackbar, withAppContext)(CompanyDetail);
